import { getNumberLabelsSprite, getNumberLabelsQuizzSprite } from './sprites';
import RenderManager from '../../rendering/render-manager';
import getRelativeCoordinates from '../../utils/get-relative-coordinates';
import SlideManager from '../../utils/slide-manager';

function addNumberLabels() {

    //Workflow :
    //1. find n for 2 ** ( n * 2 ) >= slides.length,
    //  to create the procedural sprite texture in 'makeLabelsSprite', then deduce res.
    //2. create the point material for rendering, that displays the correct sprite part
    //3. create the geometry
    //4. put labels in the scene
    //5. implements GPU picking

    /* 1. Labels params */

    let pow = 1;

    while (Math.pow(2, pow * 2) < Q3.data.slides.length) {

        pow++;

    }

    const pointSize = 40,
        numbersBySide = Math.pow(2, pow),
        totalNumbers = Math.pow(numbersBySide, 2),
        sideResolution = 128 * numbersBySide,
        sprite = getNumberLabelsSprite(Q3.data.slides.length + 1, totalNumbers, sideResolution),
        quizzSprite = getNumberLabelsQuizzSprite();//put a question mark, a tick and a cross, 256x256.

    /* 2. Labels material */
    const tested = [], sizes = [];
    Q3.data.slides.forEach((s, i) => {
        tested[i] = 0;
        sizes[i] = pointSize;
    });

    const renderMat = new THREE.ShaderMaterial({
        vertexShader: `
        attribute float ind;

        uniform float sizes[ ${ Q3.data.slides.length} ];

        varying float vInd;

        void main () {

          vInd = ind;

          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

          gl_PointSize = sizes[ int( vInd + .1 ) ];

        }
      `,
        fragmentShader: `
        varying float vInd;

        uniform float currSlide;
        uniform int mode;
        uniform sampler2D labelsSprite;
        uniform sampler2D quizzSprite;

        uniform int tested[ ${ Q3.data.slides.length} ];

        vec2 getUv () {

          float eps = .001;

          if ( mode > 0 ) {//quizz

            vec2 uv = gl_PointCoord.xy;
            uv.y = 1. - uv.y;
            uv.xy /= 2.;//wrong (tested && incorrect)

            for ( int x = 0; x < ${ Q3.data.slides.length} ; x++ ) {
              if ( x == int( vInd + eps ) ) {
                if ( tested[ x ] > 0 ) {
                  if ( abs( vInd - currSlide ) < .1 )
                    uv.xy += .5;//if tested && correct
                } else {
                  uv.y += .5;//not tested
                }
                return uv;
              }
            }

          } else {//touring

            float index = vInd + eps;
            float sideN = ${numbersBySide}.;
            vec2 uv = gl_PointCoord.xy / sideN;
            uv.x += mod( index, sideN ) / sideN;
            uv.y += floor( index / sideN ) / sideN;
            uv.y = 1. - uv.y;
            return uv;

          }

        }

        void main () {

          vec4 color;

          if ( mode > 0 ) {
            color = texture2D( quizzSprite, getUv() );
          } else {
            color = texture2D( labelsSprite, getUv() );
          }

          color.w *= .7;//opacity

          if ( color.w < .1 ) discard;//alphaTest

          //if ( gl_FragDepth < gl_FragCoord.z ) color.w *= .5;//attempt to have sketchfab labels without a full renderpass to get depth..

          if ( mode > 0 ) {//green/red colors
            for ( int x = 0; x < ${ Q3.data.slides.length} ; x++ )
              if ( x == int( vInd + .1 ) && tested[ x ] > 0 )
                color.xyz += abs( vInd - currSlide ) < .1 ? vec3( .1, .6, .12 ) : vec3( .6, .1, .12 );
          } else {
            if ( abs( vInd - currSlide ) < .1 ) color.xyz += vec3( .1, .6, .12 );//green if current slide
          }

          gl_FragColor = color;

        }
      `,
        transparent: true,
        uniforms: {
            currSlide: { type: 'f', value: SlideManager.activeSlide },
            labelsSprite: { type: 't', value: sprite },
            quizzSprite: { type: 't', value: quizzSprite },
            tested: { type: 'iv1', value: tested },
            mode: { type: 'i', value: 0 },
            sizes: { type: 'fv1', value: sizes }
        }
    });

    /* 3. Labels geometry */
    const pos = [], ind = [];

    Q3.data.slides.forEach((slide, i) => {

        pos[i * 3 + 0] = slide.labels.number.position.x;
        pos[i * 3 + 1] = slide.labels.number.position.y;
        pos[i * 3 + 2] = slide.labels.number.position.z;

        ind[i] = i;

    });

    const g = new THREE.BufferGeometry();
    g.addAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
    g.addAttribute('ind', new THREE.BufferAttribute(new Float32Array(ind), 1));

    /* 4. Put numberLabels in the scene, provide update API */

    Q3.numberLabels = new THREE.Points(g, renderMat);
    Q3.numberLabels.name = 'Number labels';

    Q3.numberLabels.correct = document.createElement('audio');
    Q3.numberLabels.correct.src = '/assets/correct.mp3';

    Q3.numberLabels.update = () => {

        Q3.numberLabels.material.uniforms.currSlide.value = SlideManager.activeSlide;
        Q3.numberLabels.material.uniforms.tested.value.forEach((v, i, a) => { a[i] = 0; });
        Q3.numberLabels.material.uniforms.mode.value = Q3.numberLabelsQuizz.enabled ? 1 : 0;

    };

    //renderOrder !==0 > rendered after everything so no transparency issue
    //(threejs renders transparent meshes in the end of the render queue,
    //then in the order they are inserted in the scene, so a background
    //transparent mesh can overlap a transparent mesh in the foreground).
    //!!! was not working for neuron ?? letting the default 0 works ?
    //Q3.numberLabels.renderOrder = 10000;

    if (Q3.data.labels.number.enabled)

        RenderManager.activeScene.add(Q3.numberLabels);

    /* 5. GPU Picking */
    //picking material
    const pickMat = new THREE.ShaderMaterial({
        vertexShader: `
        attribute float ind;
        varying float vInd;
        void main () {
          vInd = ind;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          gl_PointSize = ${pointSize}.;
        }
      `,
        fragmentShader: `
        varying float vInd;
        void main () {
          gl_FragColor = vec4( 0., 0., ( vInd + 1. ) / 255., 1. );
        }
      `
    });

    const pickingScene = new THREE.Scene();

    const gpuPickLabels = new THREE.Points(g, pickMat);
    pickingScene.add(gpuPickLabels);

    //rendertarget
    const pickingTexture = new THREE.WebGLRenderTarget(RenderManager.domElement.width, RenderManager.domElement.height);

    //reading pixel on click
    const pick = e => {

        pickingTexture.setSize(RenderManager.domElement.width, RenderManager.domElement.height);

        const dist = Math.sqrt((mouseEnd.y - mouseStart.y) ** 2 + (mouseEnd.x - mouseStart.x) ** 2);

        if (dist > maxTriggerDiff) return;

        RenderManager.mainRenderer.render(pickingScene, RenderManager.activeCamera, pickingTexture);

        const pixelBuffer = new Uint8Array(4);

        RenderManager.mainRenderer.readRenderTargetPixels(
            pickingTexture,
            mouseEnd.x * RenderManager.pixelRatio,
            RenderManager.height - mouseEnd.y * RenderManager.pixelRatio,
            1,
            1,
            pixelBuffer
        );

        if (pixelBuffer[2]) {

            const from = { pointSize: pointSize * .8 };

            TweenLite.to(
                from,
                1,
                {
                    pointSize: pointSize,
                    ease: Elastic.easeOut,
                    onUpdate() {

                        Q3.numberLabels.material.uniforms.sizes.value[pixelBuffer[2] - 1] = from.pointSize;
                        RenderManager.activeCamera.update = true;

                    }
                }
            );

            if (Q3.numberLabelsQuizz.enabled) {

                if (Q3.numberLabels.material.uniforms.tested.value[pixelBuffer[2] - 1] === 0 && pixelBuffer[2] - 1 === SlideManager.activeSlide) {

                    Q3.numberLabels.correct.play();

                    Q3.numberLabelsQuizz.update();

                }

                Q3.numberLabels.material.uniforms.tested.value[pixelBuffer[2] - 1] = 1;
                RenderManager.activeCamera.update = true;

            } else {

                SlideManager.setSlide(pixelBuffer[2] - 1);

            }

        }

    };

    let mouseStart, mouseEnd, maxTriggerDiff = 2;

    const startInteraction = e => { mouseStart = mouseEnd = getRelativeCoordinates(e); };

    const updateMouse = e => { mouseEnd = getRelativeCoordinates(e); };

    RenderManager.domElement.addEventListener('mousedown', startInteraction, false);
    RenderManager.domElement.addEventListener('touchstart', startInteraction, false);
    RenderManager.domElement.addEventListener('mousemove', updateMouse, false);
    RenderManager.domElement.addEventListener('touchmove', updateMouse, false);
    RenderManager.domElement.addEventListener('mouseup', pick, false);
    RenderManager.domElement.addEventListener('touchend', pick, false);

}

export default addNumberLabels;
export { addNumberLabels };